import { Opus } from '@streamr/opus';
import { Settings } from '@streamr/tv-engine/~/settings';
import { Track } from './Track';
/**
 * Audio decoder for the Opus codec. Decodes encoded Opus frames directly via
 * the `@streamr/opus` WASM library inside whichever worker instantiates it
 * (typically `AudioDecoderWorker`). No nested worker, no Comlink hop per
 * frame — the WASM decode runs synchronously on the calling worker's event
 * loop.
 *
 * Works on every browser; replaces the previous mix of nested-worker Opus
 * and WebCodecs Opus paths.
 */
export class OpusAudioDecoder {
    LOG_PREFIX = '[OpusAudioDecoder]';
    opus;
    unencodedOutputTrack;
    done = false;
    decodedFrameCount = 0;
    constructor(trackId, _codec = Settings.audioCodec) {
        this.unencodedOutputTrack = new Track(trackId, false);
        console.log(`${this.LOG_PREFIX} constructor called`, { trackId });
    }
    async init(_errorListener) {
        console.log(`${this.LOG_PREFIX} init called`);
        this.opus = new Opus({
            sampleRate: Settings.audioSampleRate,
            numChannels: Settings.audioNumberOfChannels,
        });
        console.log(`${this.LOG_PREFIX} init completed`);
    }
    feedEncodedAudioFrame(frame) {
        if (frame.done) {
            console.log(`${this.LOG_PREFIX} done frame received`);
            this.shutdown().catch(console.error);
            return;
        }
        if (this.done) {
            console.warn(`${this.LOG_PREFIX} is done, cannot feed more frames`);
            return;
        }
        if (!this.opus) {
            return;
        }
        try {
            const decodedData = this.opus.decode(frame.data, 'f32');
            const unencodedAudioFrame = {
                frameNumber: frame.frameNumber,
                timestamp: frame.timestamp,
                duration: frame.duration,
                trackId: frame.trackId,
                data: decodedData,
                done: frame.done,
                isKeyFrame: frame.isKeyFrame,
            };
            this.decodedFrameCount++;
            this.unencodedOutputTrack.emit(unencodedAudioFrame, false, [
                unencodedAudioFrame.data.buffer,
            ]);
        }
        catch (error) {
            console.error(`${this.LOG_PREFIX} decode error:`, error);
        }
    }
    getUnencodedOutputTrack() {
        return this.unencodedOutputTrack;
    }
    async destroy() {
        console.log(`${this.LOG_PREFIX} destroy called`);
        if (this.done)
            return;
        this.done = true;
        if (this.opus) {
            try {
                this.opus.dispose();
            }
            catch (e) {
                console.warn(`${this.LOG_PREFIX} opus.dispose failed`, e);
            }
            this.opus = undefined;
        }
    }
    async shutdown() {
        console.log(`${this.LOG_PREFIX} shutdown called`, {
            totalDecodedFrames: this.decodedFrameCount,
        });
        await this.destroy();
        this.unencodedOutputTrack.emit(undefined, true);
        console.log(`${this.LOG_PREFIX} shutdown completed`);
    }
}
